import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  Activity, Loader2, RefreshCw, AlertCircle, CheckCircle2, XCircle,
  User as UserIcon, ShoppingBag, Users as UsersIcon, MousePointerClick, Terminal,
} from "lucide-react";

const KIND_META = {
  user_registered:  { icon: UserIcon,         color: "text-emerald-300" },
  order:            { icon: ShoppingBag,      color: "text-gold" },
  inquiry:          { icon: UsersIcon,        color: "text-sky-300" },
  affiliate_click:  { icon: MousePointerClick, color: "text-rose-300" },
};

const fmtAgo = (iso) => {
  if (!iso) return "—";
  const ts = new Date(iso).getTime();
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

/* Highlight likely log severity in the backend tail */
const lineClass = (l) => {
  const s = l.toLowerCase();
  if (/error|exception|traceback/.test(s)) return "text-rose-300";
  if (/warn|warning/.test(s)) return "text-amber-300";
  if (/info|startup|started/.test(s)) return "text-cream-dim";
  return "text-cream-dim/70";
};

export default function LogsTab() {
  const [activity, setActivity] = useState(null);
  const [health, setHealth] = useState(null);
  const [logs, setLogs] = useState(null);
  const [stream, setStream] = useState("err");
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const [a, h, l] = await Promise.all([
        api.get("/admin/activity", { params: { limit: 80 } }),
        api.get("/admin/health"),
        api.get("/admin/logs", { params: { stream, lines: 200 } }),
      ]);
      setActivity(a.data);
      setHealth(h.data);
      setLogs(l.data);
    } catch (err) {
      if (err?.response?.status !== 401) console.warn("Logs tab load failed:", err);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, [stream]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!activity || !health) {
    return <div className="iq-card p-12 text-center"><Loader2 className="h-8 w-8 text-gold mx-auto animate-spin" /></div>;
  }

  const HealthRow = ({ label, ok, sub }) => (
    <div className="flex items-center justify-between py-2 border-b border-panel-border last:border-b-0">
      <div className="flex items-center gap-2">
        {ok ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-300" />
        ) : (
          <XCircle className="h-4 w-4 text-rose-400" />
        )}
        <span className="text-cream text-sm">{label}</span>
      </div>
      <span className="text-cream-dim/70 text-xs font-mono">{sub}</span>
    </div>
  );

  return (
    <div data-testid="admin-logs-panel" className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="iq-overline text-cream-dim/70 inline-flex items-center gap-2">
          <Activity className="h-3.5 w-3.5" /> Activity, errors and integration status — live
        </div>
        <button
          type="button"
          onClick={load}
          disabled={refreshing}
          data-testid="admin-logs-refresh"
          className="iq-btn-ghost text-xs"
        >
          <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* System health */}
      <div className="iq-card p-5">
        <div className="iq-overline mb-3">System health</div>
        <div className="grid md:grid-cols-2 gap-x-8">
          <HealthRow label="MongoDB ping" ok={health.db_ok} sub={health.db_ok ? "ok" : "down"} />
          <HealthRow
            label="Numbeo safety cache"
            ok={health.numbeo.cached_cities > 0}
            sub={`${health.numbeo.cached_cities} cities · ${fmtAgo(health.numbeo.last_scraped)}`}
          />
          <HealthRow
            label="UK FCDO advisory cache"
            ok={health.fcdo.cached_countries > 0}
            sub={`${health.fcdo.cached_countries} countries · ${fmtAgo(health.fcdo.last_fetched)}`}
          />
          <HealthRow
            label="Stripe API key"
            ok={health.integrations.stripe}
            sub={health.integrations.stripe ? "configured" : "missing"}
          />
          <HealthRow
            label="Stripe webhook secret"
            ok={health.integrations.stripe_webhook}
            sub={health.integrations.stripe_webhook ? "configured" : "missing"}
          />
          <HealthRow
            label="Resend (transactional email)"
            ok={health.integrations.resend}
            sub={health.integrations.resend ? "configured" : "missing"}
          />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Activity feed */}
        <div className="iq-card p-5">
          <div className="iq-overline mb-3">Activity feed</div>
          {activity.events.length === 0 ? (
            <div className="text-cream-dim/60 text-sm font-serif italic py-6 text-center">
              No activity yet. Sign up a user, place a test order, or click an affiliate link to seed.
            </div>
          ) : (
            <div className="max-h-[480px] overflow-y-auto pr-2 space-y-1">
              {activity.events.map((e, i) => {
                const meta = KIND_META[e.kind] || { icon: AlertCircle, color: "text-cream-dim" };
                const Icon = meta.icon;
                return (
                  <div key={i} className="flex items-start gap-3 py-2 px-2 hover:bg-panel-border/20 rounded-sm">
                    <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${meta.color}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-cream text-xs truncate">{e.label}</div>
                      {e.ref && <div className="text-cream-dim/60 text-[10px] font-mono truncate">{e.ref}</div>}
                    </div>
                    <div className="text-cream-dim/60 text-[10px] whitespace-nowrap mt-0.5">{fmtAgo(e.at)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Backend log tail */}
        <div className="iq-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="iq-overline inline-flex items-center gap-1"><Terminal className="h-3.5 w-3.5" /> Backend logs (last 200)</div>
            <div className="flex gap-1">
              {[
                { id: "err", label: "Errors" },
                { id: "out", label: "Output" },
              ].map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setStream(s.id)}
                  data-testid={`admin-logs-stream-${s.id}`}
                  className={`px-2 py-1 rounded-sm text-[10px] font-display tracking-widest border transition-colors ${
                    stream === s.id ? "bg-gold text-ink border-gold" : "text-cream-dim border-panel-border hover:border-gold/40"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          {logs ? (
            <pre data-testid="admin-logs-output" className="max-h-[480px] overflow-y-auto bg-ink/60 border border-panel-border rounded-sm p-3 text-[10.5px] leading-relaxed font-mono">
              {logs.warning && <div className="text-amber-300 mb-2">{logs.warning}</div>}
              {logs.lines.length === 0 ? (
                <span className="text-cream-dim/60 italic">Log stream is empty.</span>
              ) : (
                logs.lines.map((l, i) => (
                  <div key={i} className={lineClass(l)}>{l.replace(/\n$/, "")}</div>
                ))
              )}
            </pre>
          ) : (
            <div className="text-cream-dim/60 text-sm py-6 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin text-gold" /></div>
          )}
        </div>
      </div>
    </div>
  );
}
